import React, { useEffect, useRef } from "react";
import { Bold, Italic, List, ListOrdered, Palette, Underline } from "lucide-react";
import { Button } from "@/components/ui/button";

const FONT_SIZES = [
  { label: "Small", value: "2" },
  { label: "Normal", value: "3" },
  { label: "Large", value: "5" },
  { label: "Extra large", value: "6" },
];
const FONT_FAMILIES = ["Arial", "Georgia", "Verdana", "Trebuchet MS", "Times New Roman", "Courier New"];
const TEXT_COLORS = [
  { label: "Forest", value: "#047857" },
  { label: "Ocean", value: "#1d4ed8" },
  { label: "Citrus", value: "#c2410c" },
  { label: "Berry", value: "#be123c" },
  { label: "Violet", value: "#6d28d9" },
  { label: "Ink", value: "#111827" },
];

export default function RichTextEditor({ value = "", onChange, readOnly = false, ariaLabel = "Formatted text" }) {
  const editorRef = useRef(null);
  const selectionRef = useRef(null);

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value;
    }
  }, [value]);

  const saveSelection = () => {
    const selection = window.getSelection();
    if (selection?.rangeCount && editorRef.current?.contains(selection.anchorNode)) {
      selectionRef.current = selection.getRangeAt(0).cloneRange();
    }
  };

  const applyFormat = (command, commandValue) => {
    const editor = editorRef.current;
    if (!editor) return;
    editor.focus();
    const selection = window.getSelection();
    if (selectionRef.current) {
      selection.removeAllRanges();
      selection.addRange(selectionRef.current);
    }
    document.execCommand(command, false, commandValue);
    selectionRef.current = null;
    onChange?.(editor.innerHTML);
  };

  if (readOnly) {
    return (
      <div
        className="min-h-6 whitespace-pre-wrap text-sm [&_p]:my-1 [&_span]:align-baseline"
        aria-label={ariaLabel}
        dangerouslySetInnerHTML={{ __html: value }}
      />
    );
  }

  return (
    <div className="overflow-hidden rounded-md border border-input bg-background">
      <div className="flex flex-wrap items-center gap-1 border-b border-border bg-muted/40 p-1">
        <Button
          type="button"
          size="icon"
          variant="ghost"
          aria-label="Bold"
          title="Bold"
          onMouseDown={(event) => { event.preventDefault(); saveSelection(); applyFormat("bold"); }}
        >
          <Bold className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          aria-label="Underline"
          title="Underline"
          onMouseDown={(event) => { event.preventDefault(); saveSelection(); applyFormat("underline"); }}
        >
          <Underline className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          aria-label="Italic"
          title="Italic"
          onMouseDown={(event) => { event.preventDefault(); saveSelection(); applyFormat("italic"); }}
        >
          <Italic className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          aria-label="Bulleted list"
          title="Bulleted list"
          onMouseDown={(event) => { event.preventDefault(); saveSelection(); applyFormat("insertUnorderedList"); }}
        >
          <List className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          aria-label="Numbered list"
          title="Numbered list"
          onMouseDown={(event) => { event.preventDefault(); saveSelection(); applyFormat("insertOrderedList"); }}
        >
          <ListOrdered className="h-4 w-4" />
        </Button>
        <select
          aria-label={`${ariaLabel} font size`}
          defaultValue=""
          className="h-8 rounded border border-input bg-background px-2 text-xs"
          onMouseDown={saveSelection}
          onChange={(event) => {
            if (event.target.value) applyFormat("fontSize", event.target.value);
            event.target.value = "";
          }}
        >
          <option value="" disabled>Font size</option>
          {FONT_SIZES.map((size) => <option key={size.value} value={size.value}>{size.label}</option>)}
        </select>
        <select
          aria-label={`${ariaLabel} font family`}
          defaultValue=""
          className="h-8 max-w-40 rounded border border-input bg-background px-2 text-xs"
          onMouseDown={saveSelection}
          onChange={(event) => {
            if (event.target.value) applyFormat("fontName", event.target.value);
            event.target.value = "";
          }}
        >
          <option value="" disabled>Font</option>
          {FONT_FAMILIES.map((font) => <option key={font} value={font}>{font}</option>)}
        </select>
        <label className="flex h-8 items-center gap-1 rounded border border-input bg-background px-2" title="Text color">
          <Palette className="h-3.5 w-3.5" />
          <select
            aria-label={`${ariaLabel} text color`}
            defaultValue=""
            className="max-w-28 bg-transparent text-xs outline-none"
            onMouseDown={saveSelection}
            onChange={(event) => {
              if (event.target.value) applyFormat("foreColor", event.target.value);
              event.target.value = "";
            }}
          >
            <option value="" disabled>Color</option>
            {TEXT_COLORS.map((color) => <option key={color.value} value={color.value}>{color.label}</option>)}
          </select>
        </label>
      </div>
      <div
        ref={editorRef}
        role="textbox"
        aria-label={ariaLabel}
        aria-multiline="true"
        contentEditable
        suppressContentEditableWarning
        className="min-h-24 px-3 py-2 text-sm outline-none empty:before:text-muted-foreground empty:before:content-['Write_here...']"
        onInput={(event) => onChange?.(event.currentTarget.innerHTML)}
        onMouseUp={saveSelection}
        onKeyUp={saveSelection}
      />
    </div>
  );
}